import {
    getGlobalClassNames,
    HighContrastSelector,
    getFocusStyle,
    getHighContrastNoAdjustStyle,
    getRTL,
} from '@fluentui/react';
import { IGroupedComboBoxItemStyleProps, IGroupedComboBoxItemStyles } from '..';

export const submenuTopBottomPadding = 4;
export const submenuDividerTopBottomMargin = 3;
export const submenuDividerHeight = 1;

export const submenuStyles = {
    list: { paddingTop: submenuTopBottomPadding, paddingBottom: submenuTopBottomPadding },
    subComponentStyles: {
        menuItem: {
            divider: {
                margin: `${submenuDividerTopBottomMargin}px 8px`,
                [HighContrastSelector]: {
                    backgroundColor: 'WindowText',
                    ...getHighContrastNoAdjustStyle(),
                },
            },
        },
    },
};
export const GlobalClassNames = {
    root: 'ms-GroupedComboBoxButton-root',
    mainContainer: 'ms-GroupedComboBoxButton-mainContainer',
    rootTooltipHost: 'ms-GroupedComboBoxButton-root-tooltipHost',
    textContainer: 'ms-GroupedComboBoxButton-textContainer',
    icon: 'ms-GroupedComboBoxButton-icon',
    labelContainer: 'ms-GroupedComboBoxButton-labelContainer',
    labelText: 'ms-GroupedComboBoxButton-labelText',
    menuIcon: 'ms-GroupedComboBoxButton-menuIcon',
    secondaryText: 'ms-GroupedComboBoxButton-secondaryText',
    divider: 'ms-GroupedComboBoxButton-divider',
    menuButton: 'ms-GroupedComboBoxButton-menuButton',
    indicator: 'ms-GroupedComboBoxButton-indicator',
};

const wholeItemHighlightHighContrast = {
    [HighContrastSelector]: {
        ...getHighContrastNoAdjustStyle(),
        backgroundColor: 'Highlight',
        color: 'HighlightText',
        '.ms-GroupedComboBoxButton-menuButton': {
            backgroundColor: 'Highlight',
            color: 'HighlightText',
            ...getHighContrastNoAdjustStyle(),
        },
        '.ms-GroupedComboBoxButton-divider': {
            backgroundColor: 'HighlightText',
            ...getHighContrastNoAdjustStyle(),
        },
    },
};

export const GROUPED_COMBOBOX_ITEM_HEIGHT = 32;
export const getStyles = (props: IGroupedComboBoxItemStyleProps): IGroupedComboBoxItemStyles => {
    const {
        theme,
        className,
        dividerClassName,
        iconClassName,
        subMenuClassName,
        isHover,
        isPressed,
        disabled,
        reserveSpaceForIcon,
        hasIcon,
        submenuOpen,
        isSelected,
        isSubButton,
        isPrimaryButtonHover,
        showButton,
        itemHeight,
    } = props;

    const classNames = getGlobalClassNames(GlobalClassNames, theme);

    return {
        root: [
            classNames.root,
            {
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                alignItems: 'center',
                opacity: '0%',
                height: 0,
                transitionProperty: 'opacity, height',
                transitionDelay: '200ms, 0ms',
                transitionDuration: '100ms, 200ms',
            },
            showButton && {
                opacity: '100%',
                height: itemHeight,
            },
            getFocusStyle(theme, {
                highContrastStyle: {
                    outline: disabled ? '2px dotted GrayText' : '2px dashed ButtonText',
                    ...getHighContrastNoAdjustStyle(),
                },
            }),
            theme.fonts.medium,
            {
                color: theme.semanticColors.bodyText,
                backgroundColor: 'transparent',
                border: 'none',
                lineHeight: '20px',
                cursor: 'pointer',
                textAlign: 'left',
                ':focus': !disabled && wholeItemHighlightHighContrast,
            },
            disabled && {
                color: theme.semanticColors.disabledBodyText,
                cursor: 'default',
                [HighContrastSelector]: {
                    color: 'GrayText',
                    ...getHighContrastNoAdjustStyle(),
                },
            },
            isSelected &&
                !disabled && [
                    {
                        backgroundColor: theme.palette.neutralLight,
                        color: theme.semanticColors.menuItemTextHovered,
                    },
                    wholeItemHighlightHighContrast,
                ],
            // Hovering over any part of the button is lowest priority
            !isPressed &&
                !disabled &&
                isHover && {
                    backgroundColor: theme.semanticColors.menuItemBackgroundHovered,
                    color: theme.semanticColors.menuItemTextHovered,
                },
            // Submenu open style should only override non-primary button hover
            submenuOpen &&
                !disabled && {
                    backgroundColor: theme.semanticColors.menuItemBackgroundPressed,
                    color: theme.semanticColors.menuItemTextHovered,
                },
            // Hovering over the primary button
            !isPressed &&
                !disabled &&
                isHover && {
                    backgroundColor: theme.semanticColors.menuItemBackgroundHovered,
                    color: theme.semanticColors.menuItemTextHovered,
                },
            // When hovering over the textContainer the whole item should be styled in high contrast
            isPrimaryButtonHover &&
                !disabled && {
                    [HighContrastSelector]: {
                        zIndex: 1,
                        backgroundColor: 'Highlight',
                        color: 'HighlightText',
                        ...getHighContrastNoAdjustStyle(),
                        outline: '2px dashed ButtonText',
                    },
                },
            // Pressed
            !disabled &&
                isPressed && {
                    backgroundColor: theme.semanticColors.menuItemBackgroundPressed,
                    color: theme.semanticColors.menuItemTextHovered,
                    [HighContrastSelector]: {
                        zIndex: 1,
                        backgroundColor: 'Highlight',
                        color: 'HighlightText',
                        ...getHighContrastNoAdjustStyle(),
                        outline: '2px solid ButtonText',
                    },
                },
            className,
        ],
        mainContainer: [
            classNames.mainContainer,
            {
                display: 'flex',
                flexGrow: '1',
                alignItems: 'center',
                justifyContent: 'flex-start',
                minWidth: 0,
                height: '100%',
            },
            disabled && {
                ':focus': {
                    [HighContrastSelector]: {
                        backgroundColor: 'ButtonFace',
                        color: 'GrayText',
                        ...getHighContrastNoAdjustStyle(),
                    },
                },
            },
            isSubButton && {
                paddingRight: 8,
            },
        ],
        rootTooltipHost: [
            classNames.rootTooltipHost,
            {
                width: '100%',
                height: '100%',
                minWidth: 0,
            },
        ],
        textContainer: [
            classNames.textContainer,
            {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '100%',
                width: '100%',
                flexGrow: 1,
                minWidth: 0,
                overflow: 'hidden',
            },
            reserveSpaceForIcon &&
                !hasIcon && {
                    paddingLeft: 38,
                },
        ],
        icon: [
            classNames.icon,
            {
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'center',
            },
            reserveSpaceForIcon &&
                hasIcon && {
                    margin: '0px 0px 0px 6px',
                    width: '32px',
                    height: '100%',
                    minWidth: '32px',
                },
            disabled && {
                [HighContrastSelector]: {
                    color: 'GrayText',
                    ...getHighContrastNoAdjustStyle(),
                },
            },

            iconClassName,
        ],
        labelContainer: [
            classNames.labelContainer,
            {
                padding: reserveSpaceForIcon ? '0 1px 0 2px' : '0 1px 0 12px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                minWidth: '40%',
                flexGrow: 1,
                flexShrink: 4,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
            },
        ],
        labelText: [
            classNames.labelText,
            { overflow: 'hidden', textOverflow: 'clip' },
            disabled && {
                color: theme.semanticColors.disabledBodyText,
                [HighContrastSelector]: {
                    color: 'GrayText',
                    ...getHighContrastNoAdjustStyle(),
                },
            },
        ],
        menuIcon: [
            classNames.menuIcon,
            {
                width: 12,
                height: 12,
                lineHeight: 16,
                textAlign: 'center',
            },
            theme.fonts.small,
            disabled && {
                color: theme.semanticColors.disabledBodyText,
                [HighContrastSelector]: {
                    color: 'GrayText',
                    ...getHighContrastNoAdjustStyle(),
                },
            },
        ],
        secondaryText: [
            classNames.secondaryText,
            {
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                flexShrink: 5,
                paddingRight: '8px',
            },
            theme.fonts.small,
        ],
        divider: [
            classNames.divider,
            { height: '50%' },
            {
                [HighContrastSelector]: [
                    disabled && {
                        color: 'GrayText',
                        backgroundColor: 'GrayText',
                    },
                    !disabled && {
                        backgroundColor: 'windowText',
                        color: 'windowText',
                    },
                    !disabled &&
                        (isSelected || isPrimaryButtonHover || isPressed) && {
                            backgroundColor: 'highlightText',
                            color: 'highlightText',
                            borderColor: 'Highlight',
                        },
                    getHighContrastNoAdjustStyle(),
                ],
            },
            dividerClassName,
        ],
        menuButton: [
            classNames.menuButton,
            disabled && {
                backgroundColor: theme.semanticColors.listBackground,
                [HighContrastSelector]: {
                    color: 'GrayText',
                    backgroundColor: 'transparent',
                    ...getHighContrastNoAdjustStyle(),
                },
            },
            {
                color: theme.palette.neutralSecondary,
                minWidth: 28,
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'transparent',
            },
            // Lowest priority state is if the primary button is hovered
            isHover &&
                !disabled && {
                    backgroundColor: theme.semanticColors.menuItemBackgroundHovered,
                    color: theme.palette.neutralSecondary,
                },
            // Override primary button hover state if the button is active (submenu open)
            submenuOpen && {
                backgroundColor: theme.semanticColors.menuItemBackgroundPressed,
                color: theme.palette.neutralSecondary,
                [HighContrastSelector]: {
                    backgroundColor: 'Highlight',
                    color: 'HighlightText',
                    ...getHighContrastNoAdjustStyle(),
                },
            },
            // Hover directly on this button has highest priority, overrides all other styles
            !isPressed &&
                !disabled &&
                isHover && {
                    backgroundColor: theme.semanticColors.menuItemBackgroundHovered,
                    color: theme.palette.neutralSecondary,
                },
            isPressed &&
                !disabled && {
                    backgroundColor: theme.semanticColors.menuItemBackgroundPressed,
                    color: theme.palette.neutralSecondary,
                },
            {
                [HighContrastSelector]: [
                    {
                        ':hover': {
                            zIndex: 1,
                            backgroundColor: 'Highlight',
                            color: 'HighlightText',
                            outline: '2px dashed ButtonText',
                            ...getHighContrastNoAdjustStyle(),
                        },
                    },
                    (isPrimaryButtonHover || isPressed || submenuOpen) &&
                        !disabled && {
                            backgroundColor: 'Highlight',
                            color: 'HighlightText',
                            ...getHighContrastNoAdjustStyle(),
                        },
                ],
            },
            subMenuClassName,
        ],
        indicator: [
            classNames.indicator,
            {
                alignSelf: 'start',
                width: 8,
                height: 8,
            },
            getRTL(theme) && {
                transform: 'scaleX(-1)',
            },
        ],
    };
};

export const primaryButtonRightSideContainerStyle: React.CSSProperties = {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    flexShrink: 3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
};
